package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.RouteStrings
import com.milta.core.localization.WebDashboardStrings
import com.milta.feature.route.domain.entity.DriverRouteOverview
import com.milta.feature.route.domain.entity.RouteStatus
import com.milta.feature.route.domain.usecase.GetRouteDetailUseCase
import com.milta.feature.route.domain.usecase.GetTeamRoutesOverviewUseCase
import com.milta.feature.route.presentation.WebDashboardIntent
import com.milta.feature.route.presentation.WebDashboardState
import com.milta.feature.route.presentation.WebDashboardStopDisplay
import com.milta.feature.route.presentation.WebDashboardStopDisplayStatus
import com.milta.feature.route.presentation.WebDashboardTab
import com.milta.feature.route.presentation.WebDashboardViewModel
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject
import kotlin.math.roundToInt

/**
 * Entry point wired from `Main.kt`'s "Tournées du jour" nav destination (MIL-48, DOM-06 —
 * Supervision) — owns the single [WebDashboardViewModel]. Snapshot-only, no realtime/polling (see
 * the ViewModel's own doc): the initial load happens in the ViewModel's `init` block, a manual
 * [WebDashboardIntent.Refresh] is the only way to reload afterwards. Mirrors the mobile
 * [com.milta.feature.route.presentation.GestionnaireDashboardScreen]'s KPI/route-card/stop-detail
 * wording and layout (see [WebDashboardStrings]'s doc), rebuilt in Compose HTML rather than shared —
 * that screen is Compose UI (canvas), incompatible with the DOM-based web dashboard.
 */
@Composable
fun WebDashboardContent() {
    val getTeamRoutesOverview: GetTeamRoutesOverviewUseCase = koinInject()
    val getRouteDetail: GetRouteDetailUseCase = koinInject()
    val viewModel = remember { WebDashboardViewModel(getTeamRoutesOverview, getRouteDetail) }
    val state by viewModel.state.collectAsState()

    val strings = LocalStrings.current.route
    val commonStrings = LocalStrings.current.common
    val dashboardStrings = LocalStrings.current.webDashboard

    Div(
        attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "16px") } },
    ) {
        TabAndRefreshRow(state = state, strings = strings, dashboardStrings = dashboardStrings, onIntent = viewModel::onIntent)

        val error = state.error
        if (error != null) {
            InlineBanner(kind = InlineBannerKind.Error, text = dashboardErrorMessage(error, strings, commonStrings.errorOffline))
        }

        if (state.selectedTab == WebDashboardTab.Today) {
            KpiRow(state = state, strings = strings)
        }

        val overview = state.overview
        when {
            state.isLoading && overview == null -> LoadingPlaceholder(commonStrings.loading)
            overview != null && overview.routes.isEmpty() -> EmptyRoutesCard(strings)
            overview != null -> RoutesList(
                state = state,
                routes = overview.routes,
                strings = strings,
                dashboardStrings = dashboardStrings,
                offlineMessage = commonStrings.errorOffline,
                onIntent = viewModel::onIntent,
            )
        }
    }
}

@Composable
private fun TabAndRefreshRow(
    state: WebDashboardState,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    Div(
        attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between") } },
    ) {
        Div(attrs = { style { property("display", "flex"); property("gap", "8px") } }) {
            DashboardTabChip(label = strings.todayDateLabel, selected = state.selectedTab == WebDashboardTab.Today) {
                onIntent(WebDashboardIntent.SwitchTab(WebDashboardTab.Today))
            }
            DashboardTabChip(label = strings.periodTomorrowLabel, selected = state.selectedTab == WebDashboardTab.Tomorrow) {
                onIntent(WebDashboardIntent.SwitchTab(WebDashboardTab.Tomorrow))
            }
        }
        Button(
            attrs = {
                onClick { onIntent(WebDashboardIntent.Refresh) }
                style {
                    property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                    property("background", WebSurface); property("color", WebTextPrimary)
                    property("font-size", "13px"); property("font-weight", "600")
                    property("padding", "9px 16px"); property("cursor", "pointer")
                }
            },
        ) { Text("⟳ ${dashboardStrings.refreshButtonLabel}") }
    }
}

@Composable
private fun DashboardTabChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("background", if (selected) WebActiveTint else WebSurface)
                property("border", "1px solid " + (if (selected) WebTeal else WebBorder))
                property("border-radius", "50px")
                property("padding", "8px 16px"); property("cursor", "pointer")
                property("font-size", "13.5px"); property("font-weight", "700")
                property("color", if (selected) WebPrimary else WebTextSecondary)
            }
        },
    ) { Text(label) }
}

@Composable
private fun KpiRow(state: WebDashboardState, strings: RouteStrings) {
    val overview = state.overview
    val completed = overview?.completedDeliveries ?: 0
    val total = overview?.totalDeliveries ?: 0
    Div(
        attrs = { style { property("display", "grid"); property("grid-template-columns", "repeat(4, 1fr)"); property("gap", "12px") } },
    ) {
        KpiCard(label = strings.routeStatusInProgress, value = state.inProgressRoutesCount.toString())
        KpiCard(label = strings.routeStatusScheduled, value = state.scheduledRoutesCount.toString())
        KpiCard(label = strings.routeStatusCompleted, value = state.completedRoutesCount.toString())
        KpiCard(label = strings.statDeliveriesLabel, value = "$completed/$total ${strings.stopsCountLabel}")
    }
}

@Composable
private fun KpiCard(label: String, value: String) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); padding(16.px)
                property("display", "flex"); property("flex-direction", "column"); property("align-items", "center")
                property("gap", "4px")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "20px"); property("font-weight", "800"); property("color", WebTextPrimary) } },
        ) { Text(value) }
        Div(
            attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700")
                    property("letter-spacing", "0.04em"); property("text-transform", "uppercase")
                    property("color", WebTextSecondary); property("text-align", "center")
                }
            },
        ) { Text(label) }
    }
}

@Composable
private fun RoutesList(
    state: WebDashboardState,
    routes: List<DriverRouteOverview>,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    offlineMessage: String,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    val isToday = state.selectedTab == WebDashboardTab.Today
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "10px") } }) {
        routes.forEach { route ->
            val isExpanded = state.expandedRouteId == route.routeId
            RouteCard(
                route = route,
                isExpanded = isExpanded,
                isToday = isToday,
                progress = state.routeProgressByRouteId?.get(route.routeId),
                strings = strings,
                dashboardStrings = dashboardStrings,
                expandedStops = if (isExpanded) state.expandedStops else null,
                isLoadingDetail = isExpanded && state.isLoadingRouteDetail,
                detailError = if (isExpanded) state.routeDetailError else null,
                offlineMessage = offlineMessage,
                onClick = { if (isToday) onIntent(WebDashboardIntent.ExpandRoute(route.routeId)) },
            )
        }
    }
}

@Composable
private fun RouteCard(
    route: DriverRouteOverview,
    isExpanded: Boolean,
    isToday: Boolean,
    progress: Float?,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    expandedStops: List<WebDashboardStopDisplay>?,
    isLoadingDetail: Boolean,
    detailError: String?,
    offlineMessage: String,
    onClick: () -> Unit,
) {
    val driverName = "${route.driverFirstName} ${route.driverLastName}"
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); property("overflow", "hidden")
            }
        },
    ) {
        Div(
            attrs = {
                if (isToday) onClick { onClick() }
                style {
                    padding(14.px, 16.px)
                    property("cursor", if (isToday) "pointer" else "default")
                    property("display", "flex"); property("flex-direction", "column"); property("gap", "10px")
                    property("box-sizing", "border-box")
                }
            },
        ) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "12px") } }) {
                RouteAvatar(driverName)
                Div(
                    attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("flex", "1"); property("gap", "2px"); property("min-width", "0") } },
                ) {
                    Div(attrs = { style { property("font-size", "14px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(driverName) }
                    val subtitle = routeSubtitle(route, isToday, strings)
                    if (subtitle != null) {
                        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(subtitle) }
                    }
                }
                RouteStatusBadge(status = route.status, strings = strings)
            }
            if (progress != null) {
                Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
                    ProgressBar(progress = progress)
                    Div(
                        attrs = { style { property("font-size", "12px"); property("font-weight", "700"); property("color", WebTextSecondary); property("white-space", "nowrap") } },
                    ) {
                        Text("${route.completedStopsCount}/${route.totalStopsCount} ${strings.stopsCountLabel} · ${(progress * 100).roundToInt()}%")
                    }
                }
            }
        }

        if (isExpanded) {
            ExpandedStopsSection(
                stops = expandedStops,
                isLoading = isLoadingDetail,
                error = detailError,
                strings = strings,
                dashboardStrings = dashboardStrings,
                offlineMessage = offlineMessage,
            )
        }
    }
}

@Composable
private fun ProgressBar(progress: Float) {
    Div(
        attrs = {
            style {
                property("flex", "1"); height(6.px); property("border-radius", "50px")
                property("background", WebSurfaceDim); property("overflow", "hidden")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    height(6.px)
                    width(progress.coerceIn(0f, 1f).times(100).percent)
                    property("border-radius", "50px")
                    property("background", "linear-gradient(120deg, $WebTeal, $WebMint)")
                }
            },
        ) {}
    }
}

@Composable
private fun RouteStatusBadge(status: RouteStatus, strings: RouteStrings) {
    // Same RouteStatus -> color mapping as the mobile app's canonical pill (DriverHomeScreen's
    // StatusPill / GestionnaireDashboardScreen's RouteStatusPill), in hex parity with Color.kt's
    // AccentLight/Teal and InProgressBg/InProgressInk tokens. Previously "Scheduled" was neutral
    // grey and "In progress" was WebActiveTint/WebPrimary (navy-on-pale-teal, the deprecated
    // BlueDeep primary) here only, a third distinct look for the same two statuses.
    val (bg, ink, label) = when (status) {
        RouteStatus.Scheduled -> Triple(WebAccentLight, WebTealAccent, strings.routeStatusScheduled)
        RouteStatus.InProgress -> Triple(WebInProgressBg, WebInProgressInk, strings.routeStatusInProgress)
        RouteStatus.Completed -> Triple(WebSuccessBg, WebSuccessInk, strings.routeStatusCompleted)
    }
    Div(
        attrs = {
            style {
                property("background", bg); property("color", ink)
                property("border-radius", "50px"); property("padding", "5px 12px")
                property("font-size", "11.5px"); property("font-weight", "700"); property("white-space", "nowrap")
            }
        },
    ) { Text(label) }
}

@Composable
private fun RouteAvatar(name: String) {
    val label = initials(name)
    Div(
        attrs = {
            style {
                width(38.px); height(38.px)
                property("border-radius", "50%")
                property("background", avatarColorFor(label))
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("flex-shrink", "0")
                property("color", "#FFFFFF"); property("font-weight", "700"); property("font-size", "13px")
            }
        },
    ) { Text(label) }
}

@Composable
private fun ExpandedStopsSection(
    stops: List<WebDashboardStopDisplay>?,
    isLoading: Boolean,
    error: String?,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    offlineMessage: String,
) {
    Div(
        attrs = {
            style {
                property("border-top", "1px solid $WebSurfaceDim")
                property("background", WebSurfaceDim)
                padding(14.px, 16.px)
                property("display", "flex"); property("flex-direction", "column"); property("gap", "8px")
                property("box-sizing", "border-box")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700"); property("letter-spacing", "0.05em")
                    property("text-transform", "uppercase"); property("color", WebTextSecondary)
                }
            },
        ) { Text(dashboardStrings.expandedStopsTitle) }

        when {
            error != null -> InlineBanner(kind = InlineBannerKind.Error, text = routeDetailErrorMessage(error, strings, offlineMessage))
            isLoading || stops == null -> Div(
                attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } },
            ) { Text("…") }
            else -> stops.forEach { stop -> StopRow(stop = stop, strings = strings, dashboardStrings = dashboardStrings) }
        }
    }
}

private data class StopStatusStyle(val bg: String, val ink: String, val icon: String, val label: String)

@Composable
private fun StopRow(stop: WebDashboardStopDisplay, strings: RouteStrings, dashboardStrings: WebDashboardStrings) {
    val style = when (stop.displayStatus) {
        WebDashboardStopDisplayStatus.Delivered -> StopStatusStyle(WebSuccessBg, WebSuccessInk, "✓", strings.stopStatusDeliveredLabel)
        WebDashboardStopDisplayStatus.EnRoute -> StopStatusStyle(WebActiveTint, WebPrimary, "→", strings.stopStatusEnRouteLabel)
        WebDashboardStopDisplayStatus.Waiting -> StopStatusStyle(WebSurfaceDim, WebTextSecondary, "○", dashboardStrings.stopStatusWaitingLabel)
        WebDashboardStopDisplayStatus.Failed -> StopStatusStyle(WebErrorBg, WebErrorInk, "✗", dashboardStrings.stopStatusFailedLabel)
        WebDashboardStopDisplayStatus.Untreated -> StopStatusStyle(WebWarningBg, WebWarning, "⊘", strings.stopStatusUntreatedLabel)
    }
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                property("background", WebSurface); property("border-radius", "8px")
                padding(10.px, 12.px); property("box-sizing", "border-box")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", style.bg); property("color", style.ink)
                    property("border-radius", "50px"); property("padding", "3px 9px")
                    property("font-size", "11px"); property("font-weight", "700"); property("white-space", "nowrap")
                }
            },
        ) { Text("${style.icon} ${style.label}") }
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("flex", "1"); property("gap", "1px"); property("min-width", "0") } }) {
            Div(attrs = { style { property("font-size", "13px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                Text(stop.delivery.recipientName.ifBlank { strings.unknownRecipientLabel })
            }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(stop.delivery.address) }
        }
    }
}

@Composable
private fun EmptyRoutesCard(strings: RouteStrings) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); padding(40.px, 24.px)
                property("text-align", "center"); property("box-sizing", "border-box")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } },
        ) { Text(strings.emptyTeamRoutesTitle) }
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(strings.emptyTeamRoutesSubtitle) }
    }
}

@Composable
private fun LoadingPlaceholder(label: String) {
    Div(
        attrs = { style { property("padding", "40px"); property("text-align", "center"); property("color", WebTextSecondary); property("font-size", "13px") } },
    ) { Text(label) }
}

/** "2026-08-06T08:12:00+00:00" -> "08:12" — mirrors [com.milta.feature.route.presentation.GestionnaireDashboardScreen]'s
 * private `formatTime`. */
private fun formatTime(iso: String): String {
    val timePart = iso.substringAfter('T', missingDelimiterValue = "")
    return if (timePart.length >= 5) timePart.take(5) else iso
}

/**
 * "Démarré à 08:12" / "Terminé à 12:47" / "Non démarrée" on [WebDashboardTab.Today] (mirrors the
 * mobile Gestionnaire dashboard's own card subtitle exactly). On [WebDashboardTab.Tomorrow], the
 * ticket only asks for "l'heure si déjà dispo" — a not-yet-started future route (the normal case)
 * shows no subtitle at all rather than a "Non démarrée" that would just be noise for a route that
 * hasn't happened yet.
 */
private fun routeSubtitle(route: DriverRouteOverview, isToday: Boolean, strings: RouteStrings): String? {
    val completedAt = route.completedAt
    val startedAt = route.startedAt
    return when {
        // Cross-module smart cast isn't allowed on completedAt/startedAt (declared in :shared),
        // hence the local val bindings above.
        isToday && route.status == RouteStatus.Completed && completedAt != null ->
            "${strings.routeCompletedAtPrefix} ${formatTime(completedAt)}"
        startedAt != null -> "${strings.routeStartedAtPrefix} ${formatTime(startedAt)}"
        isToday -> strings.routeNotStartedLabel
        else -> null
    }
}

/** Mirrors [WebDashboardViewModel.mapError]'s codes exactly. */
private fun dashboardErrorMessage(code: String, strings: RouteStrings, offlineMessage: String): String = when (code) {
    "invalid_date" -> strings.errorInvalidDate
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}

/** Mirrors [WebDashboardViewModel.mapRouteDetailError]'s codes exactly. */
private fun routeDetailErrorMessage(code: String, strings: RouteStrings, offlineMessage: String): String = when (code) {
    "route_not_found" -> strings.errorRouteNotFound
    "forbidden" -> strings.errorForbidden
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}
